/**
 * Expands by edges + nodes up to n hops (incoming/outgoing/both)
 * Initial edge expansion must happen to get endpoints
 * Should be fast on most big graphs: nodeIndex+edgeIndex wavefront culls repeats (cycles, ...)
 * Result is a mutation of highlightEdges and highlightNodes with added hits
 * 
 * TODO: user-driven + auto threshold/sampling strategies for big nodes, selections?
 * TODO: memoize last k >= 1 hits, & invalidate upon vbo update
 * @param {Object} args
 * @param {number[]} args.highlightedNodeIndices
 * @param {number[]} args.highlightedEdgeIndices
 * @param {number} args.neighborhoodHighlightHops
 * @param {'outgoing'|'incoming'|'both'|'none'} args.neighborhoodHighlight
 * @param {Int32Array} args.forwardsEdgeStartEndIdxs
 * @param {Int32Array} args.backwardsEdgeStartEndIdxs
 * @param {Uint32Array} args.forwardsEdgeToUnsortedEdge
 * @param {Uint32Array} args.backwardsEdgeToUnsortedEdge
 * @param {Uint32Array} args.logicalEdges
 * @param {number} args.numPoints - Number of points in the graph
 * @returns {void}
 */
export function wavefrontExpand({
  highlightedNodeIndices,
  highlightedEdgeIndices,
  neighborhoodHighlightHops,
  neighborhoodHighlight,
  forwardsEdgeStartEndIdxs,
  backwardsEdgeStartEndIdxs,
  forwardsEdgeToUnsortedEdge,
  backwardsEdgeToUnsortedEdge,
  logicalEdges,
  numPoints,
}) {
  const outgoingMemo = { edges: [], length: 0 };
  const incomingMemo = { edges: [], length: 0 };
  // Extend edges with neighbors of nodes
  let highlightedNodeIndicesIndex = highlightedNodeIndices.length;
  let highlightedEdgeIndicesIndex = highlightedEdgeIndices.length;

  const hitEdges = new Set(highlightedEdgeIndices);
  const hitNodes = new Set(highlightedNodeIndices);
  const exploredNodes = new Set();

  let prevNodeEdgeHits = 0;
  let nodeIndex = -1; // resume each hop where previous left off
  let edgeIndex = -1; // resume each hop where previous left off
  for (let hop = 0; hop < neighborhoodHighlightHops; hop++) {
    // Extend edge indices with prev hop nodes' incoming/outgoing edges
    for (let length = highlightedNodeIndices.length; ++nodeIndex < length; ) {
      if (neighborhoodHighlight == "none") {
        break;
      }

      const node = highlightedNodeIndices[nodeIndex];
      if (exploredNodes.has(node)) {
        continue;
      }
      exploredNodes.add(node);

      const {
        outgoingMemo: { edges: outgoingEdges, length: outgoingEdgesLength },
        incomingMemo: { edges: incomingEdges, length: incomingEdgesLength },
      } = getSortedConnectedEdges(
        node,
        forwardsEdgeStartEndIdxs,
        backwardsEdgeStartEndIdxs,
        neighborhoodHighlight,
        outgoingMemo,
        incomingMemo
      );
      for (let index2 = -1; ++index2 < outgoingEdgesLength; ) {
        const outEdge = forwardsEdgeToUnsortedEdge[outgoingEdges[index2]];
        if (hitEdges.has(outEdge)) {
          continue;
        }
        hitEdges.add(outEdge);
        highlightedEdgeIndices[highlightedEdgeIndicesIndex++] = outEdge;
      }
      for (let index2 = -1; ++index2 < incomingEdgesLength; ) {
        const backEdge = backwardsEdgeToUnsortedEdge[incomingEdges[index2]];
        if (hitEdges.has(backEdge)) {
          continue;
        }
        hitEdges.add(backEdge);
        highlightedEdgeIndices[highlightedEdgeIndicesIndex++] = backEdge;
      }
    }

    // Extend node indices with edge endpoints since last additions
    for (let length = highlightedEdgeIndices.length; ++edgeIndex < length; ) {
      const stride = 2 * highlightedEdgeIndices[edgeIndex];
      const src = logicalEdges[stride];
      const dst = logicalEdges[stride + 1];
      if (!(hitNodes.has(src))) {
        hitNodes.add(src);
        highlightedNodeIndices[highlightedNodeIndicesIndex++] = src;
      }
      if (!(hitNodes.has(dst))) {
        hitNodes.add(dst);
        highlightedNodeIndices[highlightedNodeIndicesIndex++] = dst;
      }
    }

    if (highlightedNodeIndices.length == numPoints) {
      break; // guaranteed fixed point; detect early
    }
    const curHits =
      highlightedNodeIndices.length + highlightedEdgeIndices.length;
    if (curHits === prevNodeEdgeHits) {
      break; // fixed point, no more progress possible
    }
    prevNodeEdgeHits = curHits;
  } // for neighborhoodHighlightHops
}

/**
 * Modify incoming and outgoing memo with the edges connected to nodeId.
 * @param {number} nodeId
 * @param {Int32Array} forwardsEdgeStartEndIdxs
 * @param {Int32Array} backwardsEdgeStartEndIdxs
 * @param {'outgoing'|'incoming'|'both'} neighborhoodHighlight
 * @param {{edges: number[], length: number}} outgoingMemo
 * @param {{edges: number[], length: number}} incomingMemo
 * @returns {{outgoingMemo: {edges: number[], length: number}, incomingMemo: {edges: number[], length: number}}}
 */
function getSortedConnectedEdges(
  nodeId,
  forwardsEdgeStartEndIdxs,
  backwardsEdgeStartEndIdxs,
  neighborhoodHighlight,
  outgoingMemo = { edges: [], length: 0 },
  incomingMemo = { edges: [], length: 0 }
) {
  if (
    neighborhoodHighlight === "outgoing" ||
    neighborhoodHighlight === "both"
  ) {
    let index = -1;
    let edges = outgoingMemo.edges;
    let edge = forwardsEdgeStartEndIdxs[2 * nodeId + 0];
    const stop = forwardsEdgeStartEndIdxs[2 * nodeId + 1];
    outgoingMemo.length = stop - edge;
    while (edge < stop) {
      edges[++index] = edge++;
    }
  } else {
    outgoingMemo.length = 0;
  }
  if (
    neighborhoodHighlight === "incoming" ||
    neighborhoodHighlight === "both"
  ) {
    let index2 = -1;
    let edges2 = incomingMemo.edges;
    let edge2 = backwardsEdgeStartEndIdxs[2 * nodeId + 0];
    const stop2 = backwardsEdgeStartEndIdxs[2 * nodeId + 1];
    incomingMemo.length = stop2 - edge2;
    while (edge2 < stop2) {
      edges2[++index2] = edge2++;
    }
  } else {
    incomingMemo.length = 0;
  }
  return { outgoingMemo, incomingMemo };
}

/**
 * Get the indices of the (unsorted) interior edges for a set of nodes
 * Prepass guides preallocation
 * @param {number[]} nodeIndices
 * @param {Int32Array} forwardsEdgeStartEndIdxs
 * @param {Uint32Array} forwardsEdgeToUnsortedEdge
 * @param {Uint32Array} logicalEdges
 */
export function getInteriorEdges(nodeIndices, forwardsEdgeStartEndIdxs, forwardsEdgeToUnsortedEdge, logicalEdges) {
  
  const nodeSet = new Set(nodeIndices);

  let numInteriorEdges = 0;
  for (let index = -1; ++index < nodeIndices.length; ) {
    const nodeId = nodeIndices[index];
    const start = forwardsEdgeStartEndIdxs[2 * nodeId + 0];
    const stop = forwardsEdgeStartEndIdxs[2 * nodeId + 1];

    let edge = start;
    while (edge < stop) {
      const unsortedEdge = forwardsEdgeToUnsortedEdge[edge];
      const dstIdx = logicalEdges[2 * unsortedEdge + 1];
      if (nodeSet.has(dstIdx)) {
        numInteriorEdges++;
      }
      edge++;
    }

  }

  if (numInteriorEdges === 0) {
    return [];
  }

  const interiorEdges = new Uint32Array(numInteriorEdges);
  let offset = 0;
  for (let index = -1; ++index < nodeIndices.length; ) {
    const nodeId = nodeIndices[index];

    let edge = forwardsEdgeStartEndIdxs[2 * nodeId + 0];
    const stop = forwardsEdgeStartEndIdxs[2 * nodeId + 1];
    while (edge < stop) {
      const unsortedEdge = forwardsEdgeToUnsortedEdge[edge];
      if (nodeSet.has(logicalEdges[2 * unsortedEdge + 1])) {
        interiorEdges[offset++] = unsortedEdge;
      }
      edge++;
    }
  }

  //TODO make downstream support Uint32Array
  return Array.from(interiorEdges);
}